import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { Skeleton } from '@/ui/skeleton'
import { Button } from '@/ui/button'
import { MaterialIcon } from '@/ui/material-icon'
import { getInstanceSchema } from '@/features/adp/services/api-service'
import type { RegisteredSourcePickerItem } from '@/features/adp/hooks/useRegisteredDataSources'
import type { SourceFile, SourceFileField } from '@/features/adp/services/api-service.types'
import { supportsSchemaForInstance } from '@/features/adp/components/sheets/ConfigureDataDestinationSheet/helpers'
import styles from './ConfigureDataSourceSheet.module.scss'

export type RegisteredExistingSourceSelection = {
  tableName: string
  sourceFileId: number
  sourceDataSourceId: number
  rooId: number
  fields: SourceFileField[]
}

type RegisteredSourceDetailsCardProps = Readonly<{
  registeredSource: RegisteredSourcePickerItem
  onAddNew?: () => void
  onSaveExistingSelection?: (selection: RegisteredExistingSourceSelection) => void
  isSavingSelection?: boolean
}>

function asDisplayText(value: unknown): string {
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number') return String(value)
  return ''
}

function parseSchemaFromEndpoint(endpoint: string): string {
  const trimmed = endpoint.trim()
  if (!trimmed) return ''
  const q = trimmed.indexOf('?')
  return q >= 0 ? trimmed.slice(q + 1).trim() : ''
}

function classifySourceType(rawType: string, rawInstance: string): 'rdbms' | 'nosql' | 'file' | 'rest' {
  const type = rawType.trim().toLowerCase()
  const instance = rawInstance.trim().toLowerCase()
  if (type === 'rdbms' || type === 'database' || type === 'table') return 'rdbms'
  if (type === 'no sql' || type === 'nosql' || instance === 'mongodb') return 'nosql'
  if (
    type === 'file' ||
    type === 'filesystem' ||
    type === 'gen2filesystem' ||
    instance === 'excel' ||
    instance === 'sftpserver'
  ) {
    return 'file'
  }
  return 'rest'
}

function resolveListItemLabel(
  sourceClass: 'rdbms' | 'nosql' | 'file' | 'rest',
  sourceInstance: string,
  sourceFiles: SourceFile[]
): 'Table' | 'File' | 'Sheet' {
  if (sourceClass !== 'file') return 'Table'

  const instance = sourceInstance.trim().toLowerCase()
  if (instance === 'excel') return 'Sheet'

  if (sourceFiles.length > 0 && sourceFiles[0]?.source_file_type === '.xlsx') {
    return 'Sheet'
  }
  return 'File'
}

export function RegisteredSourceDetailsCard({
  registeredSource,
  onAddNew,
  onSaveExistingSelection,
  isSavingSelection = false,
}: RegisteredSourceDetailsCardProps) {
  const [searchText, setSearchText] = useState('')
  const [selectedTableName, setSelectedTableName] = useState('')
  const [expandedFileName, setExpandedFileName] = useState('')

  const instanceSchemaQuery = useQuery({
    queryKey: [
      'adp',
      'source-data-sources',
      'instance-schema',
      registeredSource.id,
      registeredSource.dataSourceInstance,
    ],
    enabled: Boolean(registeredSource.id.trim() && registeredSource.dataSourceInstance.trim()),
    queryFn: () => getInstanceSchema(registeredSource.id, registeredSource.dataSourceInstance),
  })

  const instanceDetails = instanceSchemaQuery.data?.data?.[0]?.instance_details?.[0]
  const sourceName = asDisplayText(instanceDetails?.data_source_name) || registeredSource.connectionName
  const sourceTypeRaw = asDisplayText(instanceDetails?.data_source_type) || registeredSource.dataSourceType
  const sourceInstance = asDisplayText(instanceDetails?.data_source_instance) || registeredSource.dataSourceInstance
  const sourceClass = classifySourceType(sourceTypeRaw, sourceInstance)
  const bucketName = asDisplayText(instanceDetails?.bucket_name)
  const fileName = asDisplayText(instanceDetails?.source_files?.[0]?.file_name)
  const databaseName = asDisplayText(instanceDetails?.database_name)
  const supportsSchema = supportsSchemaForInstance(sourceInstance)
  const schemaName = supportsSchema
    ? parseSchemaFromEndpoint(asDisplayText(instanceDetails?.endpoint))
    : ''
  const sourceFiles: SourceFile[] = instanceDetails?.source_files ?? []
  const fileTypeLabel = resolveListItemLabel(sourceClass, sourceInstance, sourceFiles)
  const isExcelSource = sourceInstance.toLowerCase() === 'excel'
  const addNewButtonVisible = !['restapi-realtime', 'restapi'].includes(sourceInstance.toLowerCase())
  const filteredSourceFiles = useMemo(() => {
    const search = searchText.trim().toLowerCase()
    if (!search) return sourceFiles
    return sourceFiles.filter(file => file.file_name.toLowerCase().includes(search))
  }, [searchText, sourceFiles])
  const loadErrorMessage =
    instanceSchemaQuery.error instanceof Error
      ? instanceSchemaQuery.error.message
      : 'Failed to load registered source details'
  const selectedSourceFile =
    sourceFiles.find(file => file.file_name === selectedTableName) ?? null

  const handleSaveSelection = () => {
    if (!selectedSourceFile || !instanceDetails) return
    onSaveExistingSelection?.({
      tableName: selectedSourceFile.file_name,
      sourceFileId: selectedSourceFile.source_file_id,
      sourceDataSourceId: instanceDetails.source_data_source_id,
      rooId: instanceDetails.roo_id,
      fields: selectedSourceFile.source_file_fields,
    })
  }

  const handleToggleSelectedTable = (fileName: string) => {
    setSelectedTableName(prev => (prev === fileName ? '' : fileName))
  }

  let content: ReactNode
  if (instanceSchemaQuery.isPending) {
    content = (
      <>
        <Skeleton style={{ height: 34, width: '100%' }} />
        <Skeleton style={{ height: 34, width: '100%' }} />
        <Skeleton style={{ height: 34, width: '100%' }} />
        <Skeleton style={{ height: 110, width: '100%' }} />
      </>
    )
  } else if (instanceSchemaQuery.isError) {
    content = (
      <p className={styles.errorText} role="alert">
        {loadErrorMessage}
      </p>
    )
  } else {
    content = (
      <>
        <div className={styles.field}>
          <div className={styles.fieldLabel}>Source Name</div>
          <input className={styles.textInput} type="text" value={sourceName} readOnly />
        </div>
        {sourceClass === 'file' && sourceInstance.toLowerCase() === 'excel' && (
          <div className={styles.field}>
            <div className={styles.fieldLabel}>File Name</div>
            <input className={styles.textInput} type="text" value={fileName} readOnly />
          </div>
        )}
        {sourceClass === 'file' && sourceInstance.toLowerCase() !== 'excel' && bucketName && (
          <div className={styles.field}>
            <div className={styles.fieldLabel}>Bucket Name</div>
            <input className={styles.textInput} type="text" value={bucketName} readOnly />
          </div>
        )}
        {sourceClass === 'rdbms' && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: supportsSchema ? '1fr 1fr' : '1fr',
              gap: 12,
            }}
          >
            <div className={styles.field}>
              <div className={styles.fieldLabel}>Database Name</div>
              <input className={styles.textInput} type="text" value={databaseName} readOnly />
            </div>
            {supportsSchema && (
              <div className={styles.field}>
                <div className={styles.fieldLabel}>Schema Name</div>
                <input className={styles.textInput} type="text" value={schemaName} readOnly />
              </div>
            )}
          </div>
        )}
        {sourceClass === 'nosql' && sourceInstance.toLowerCase() !== 'neo4j' && (
          <div className={styles.field}>
            <div className={styles.fieldLabel}>Database Name</div>
            <input className={styles.textInput} type="text" value={databaseName} readOnly />
          </div>
        )}
        {sourceClass === 'rest' && (
          <div className={styles.field}>
            <div className={styles.fieldLabel}>Database Name</div>
            <input className={styles.textInput} type="text" value={databaseName} readOnly />
          </div>
        )}
        {sourceFiles.length > 0 && (
          <div className={styles.registeredTableCard}>
            <div className={styles.registeredTableHeader}>
              <div className={styles.registeredTableTitle}>{fileTypeLabel} List</div>
              <div className={styles.registeredTableActions}>
                <input
                  className={`${styles.textInput} ${styles.registeredSearchInput}`}
                  type="text"
                  placeholder={`Search by ${fileTypeLabel.toLowerCase()} name`}
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                />
                {addNewButtonVisible && (
                  <Button
                    type="button"
                    onClick={onAddNew}
                    className={styles.registeredActionBtn}
                    disabled={isExcelSource || Boolean(selectedTableName)}
                  >
                    Add New {fileTypeLabel}
                  </Button>
                )}
              </div>
            </div>
            <div className={styles.registeredListHeader}>
              <span>{fileTypeLabel} Name</span>
              <span>Expand</span>
            </div>
            <div className={styles.registeredTableRows}>
              {filteredSourceFiles.map(file => {
                const isExpanded = expandedFileName === file.file_name
                return (
                  <div
                    key={`${file.source_file_id}-${file.file_name}`}
                    className={styles.registeredTableRowWrap}
                  >
                    <button
                      type="button"
                      className={styles.registeredTableRow}
                      onClick={() =>
                        setExpandedFileName(prev => (prev === file.file_name ? '' : file.file_name))
                      }
                      aria-expanded={isExpanded}
                    >
                      <label className={styles.registeredTableSelect}>
                        <button
                          type="button"
                          className={styles.registeredTableRadioBtn}
                          role="radio"
                          aria-checked={selectedTableName === file.file_name}
                          aria-label={`Select ${file.file_name}`}
                          onClick={e => {
                            e.stopPropagation()
                            handleToggleSelectedTable(file.file_name)
                          }}
                        >
                          <MaterialIcon
                            name={
                              selectedTableName === file.file_name
                                ? 'radio_button_checked'
                                : 'radio_button_unchecked'
                            }
                            size={18}
                          />
                        </button>
                        <span>{file.file_name}</span>
                      </label>
                      <span className={styles.registeredExpandBtn} aria-hidden>
                        <MaterialIcon name={isExpanded ? 'remove' : 'add'} size={18} />
                      </span>
                    </button>
                    {isExpanded && (
                      <div className={styles.registeredFieldRows}>
                        <div className={styles.registeredFieldsTable}>
                          <div className={styles.registeredFieldsHeaderCell}>Field Name</div>
                          <div className={styles.registeredFieldsHeaderCell}>Data Type</div>
                          {file.source_file_fields.map(field => (
                            <div
                              key={`${file.source_file_id}-${field.source_file_field_id}`}
                              className={styles.registeredFieldsRowPair}
                            >
                              <div className={styles.registeredFieldsBodyCell}>{field.field_name}</div>
                              <div className={styles.registeredFieldsBodyCell}>{field.field_data_type}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
              {filteredSourceFiles.length === 0 && (
                <p className={styles.registeredTableState}>No {fileTypeLabel.toLowerCase()} found.</p>
              )}
            </div>
            <div className={styles.registeredSaveRow}>
              <Button
                type="button"
                onClick={handleSaveSelection}
                className={styles.registeredActionBtn}
                disabled={!selectedSourceFile || isSavingSelection}
              >
                {isSavingSelection ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <div className={styles.formCard}>
      {content}
    </div>
  )
}
